/*
  Итог по счетам и кнопка переноса - вторая строка варианта Б.

  Кнопка одна (§1.3.1): перенести все счета с собранным минимумом. Решение
  владельца 30.09.2026 - переносит человек, система не выбирает за него. Отказ
  по файлу показывается словами сервера.

  Ошибка переноса стоит здесь, под кнопкой, - рядом с действием.
*/

import { accounts, dateTime, FACT_TITLES, rows } from '../../api/format.js'
import { Lock } from '../AccessContext.jsx'
import { Loading, Notice } from '../Common.jsx'
import Disclosure from '../Disclosure.jsx'
import PickAccounts from '../picker/PickAccounts.jsx'
import useTransferPick from './useTransferPick.js'

function Missing({ groups }) {
  if (!groups.length) return null
  return (
    <ul className="list">
      {groups.map((group) => (
        <li key={group.missing.join('+')}>
          не хватает: {group.missing.map((fact) => FACT_TITLES[fact] ?? fact).join(', ')} -{' '}
          {accounts(group.accounts)}
        </li>
      ))}
    </ul>
  )
}

// Что легло в портфель помимо счетов и начислений - иначе сверка скана
// выглядела бы потраченной впустую (осмотр 30.09.2026).
const SIDE_TITLES = [
  ['residents', 'в домовую книгу'],
  ['identities', 'документы людей'],
  ['contacts', 'телефоны и почта'],
  ['premises', 'сведения о помещениях'],
  ['bankruptcy', 'сведения о банкротстве'],
]

/*
  Пара «что - сколько» не рвётся строкой (указание владельца 02.10.2026): строка
  итога переносилась посреди пары, и «- 1.» уезжало на следующую строку одно,
  без подписи. Пробелы внутри пары неразрывные, поэтому перенос возможен только
  после «;» - между парами.
*/
// Неразрывный пробел - кодом символа, а не самим символом: в исходнике он
// неотличим от обычного, и форматер однажды уже заменил им запись кодом.
const NBSP = String.fromCharCode(0xa0)
const together = (text) => text.replaceAll(' ', NBSP)

function sides(report) {
  return SIDE_TITLES.filter(([key]) => report[key])
    .map(([key, title]) => `; ${together(`${title} - ${report[key]}`)}`)
    .join('')
}

// Перенос идёт фоновой работой (01.10.2026): кнопка ставит его в очередь, а
// вкладка сама переспрашивает, пока он идёт. Закрыть страницу можно.
function Running({ transfer }) {
  return (
    <Notice kind="ok">
      <div className="notice__what">Перенос идёт с {dateTime(transfer.requested_at)}.</div>
      <div className="notice__do">
        Страницу можно закрыть - перенос доведёт Арбитр ЖКХ, а итог появится здесь.
      </div>
    </Notice>
  )
}

// Причину неудачи пишет сервер целиком - что случилось и что делать.
function Failed({ transfer }) {
  return (
    <Notice kind="error">
      <div className="notice__what">Перенос {dateTime(transfer.requested_at)} не удался.</div>
      <div className="notice__do">{transfer.error_message}</div>
    </Notice>
  )
}

/*
  Сколько разных замечаний хранит сервер (`upload_transfer_service.ISSUES_KEPT`).
  Ровно столько - значит, остальные отрезаны, и голое «50» читалось бы как «их
  всего пятьдесят» (01.10.2026, проверка перед выкладкой).
*/
export const ISSUES_KEPT = 50

const issuesCounter = (count) =>
  count >= ISSUES_KEPT ? `показаны первые ${ISSUES_KEPT} видов замечаний` : String(count)

/*
  🔴 Кнопки «Объём ожидаем, перенести» с 19.10.2026 нет (решение владельца):
  сторож объёма перенос не останавливает, а при выгрузке «в разы меньше
  прошлой» откладывает погашение пропавших месяцев - об этом говорит замечание
  переноса, а решение стоит в журнале загрузок шага «Начисления по месяцам».
  Оставшиеся отказы (файл пропал из хранилища) подтверждением не лечатся.
*/
function Report({ transfer }) {
  if (!transfer) return null
  if (transfer.state === 'running') return <Running transfer={transfer} />
  if (transfer.state === 'failed') return <Failed transfer={transfer} />
  const { report } = transfer
  const refused = (report.files ?? []).filter((file) => file.refused)
  const written = (report.files ?? []).filter((file) => !file.refused)
  return (
    <div className="stack">
      <p className="muted filename">
        Последний перенос: {together(`новых счетов - ${report.opened ?? 0}`)}
        {/* Имя файла переносится как обычно - оно бывает длиннее строки; к
            нему пристёгнуто только «- записано N строк». */}
        {written
          .map((file) => `; «${file.filename}»${together(` - записано ${rows(file.accepted)}`)}`)
          .join('')}
        {sides(report)}.
      </p>
      {refused.map((file) => (
        // Отчёты, сохранённые до 01.10.2026, `file_id` не несут - у них ключ по имени.
        <Notice key={file.file_id ?? file.filename} kind="warn">
          <div className="notice__what filename">«{file.filename}»: {file.refused}</div>
        </Notice>
      ))}
      {report.issues?.length ? (
        <Disclosure title="Замечания переноса" counter={issuesCounter(report.issues.length)}>
          <ul className="list">
            {/* Одинаковые замечания свёрнуты сервером в одно с числом строк,
                поэтому текст замечания уникален и годится ключом. */}
            {report.issues.map((issue) => (
              <li key={issue.text}>
                {issue.text} <span className="muted">- {rows(issue.count)}</span>
              </li>
            ))}
          </ul>
        </Disclosure>
      ) : null}
    </div>
  )
}

/*
  `note` - подпись, которую ставит рядом с кнопкой тот, кто показывает панель не
  на своей вкладке (02.10.2026: шаг 3 «Домовая книга»). Она живёт ВНУТРИ
  карточки: вынесенная наружу, она висит на фоне страницы и читается как
  подпись к следующему блоку, а не к кнопке переноса. Во вкладке «Файлы»
  подписи нет - там человек и так стоит перед своим накопителем.
*/
/* Подпись над числом, а не глагол после него: «6 021 счёт ждут» не
   согласуется, а подпись с числом читается при любом числе. */
function Totals({ summary, busy, running, onTransfer, pick }) {
  const { ready, waiting, conflicts } = summary
  const label = pick.only
    ? `Перенести выбранные: ${accounts(pick.only.length)}`
    : `Перенести ${accounts(ready)} в портфель`
  return (
    <div className="row row--between row--choices">
        <div>
          <div className="money__label">Готовы к переносу</div>
          <div className="money__value">{accounts(ready)}</div>
          <div className="muted">собраны все обязательные сведения</div>
        </div>
        <div>
          <div className="money__label">Ждут сведений</div>
          <div className="money__value">{accounts(waiting)}</div>
          <div className="muted">пришлите файлы с тем, чего не хватает</div>
        </div>
        {conflicts ? (
          <div>
            <div className="money__label">Спорные</div>
            <div className="money__value">{accounts(conflicts)}</div>
            <div className="muted">файлы называют разное - выберите верное ниже</div>
          </div>
        ) : null}
        <Lock>
          <div className="row row--tight">
            <PickAccounts
              choices={pick.choices}
              only={pick.only}
              onChange={pick.setOnly}
              onNeed={pick.need}
              disabled={busy || running || ready === 0}
              note={`Готовы к переносу: ${accounts(ready)}`}
              amountTitle="Долг по расчёту УК"
            />
            <button
              type="button"
              className="button"
              disabled={busy || running || ready === 0}
              onClick={() => onTransfer(false, pick.only)}
            >
              {busy || running ? 'Перенос идёт...' : label}
            </button>
          </div>
        </Lock>
    </div>
  )
}

/*
  `summary` бывает пустым (02.10.2026): сводка приходит своим запросом и
  считается дольше списка. Ход переноса - «идёт», «не удался», итог - от неё не
  зависит и виден сразу; числа и кнопка встают, когда она пришла.
*/
export default function TransferPanel({ summary, transfer, onTransfer, busy, error, note }) {
  const running = transfer?.state === 'running'
  // Выбор счетов окном (16.10.2026); `onTransfer(force, only)` - пусто значит все готовые.
  const pick = useTransferPick(summary?.ready)
  return (
    <section className="section card">
      {summary ? (
        <Totals
          summary={summary}
          busy={busy}
          running={running}
          onTransfer={onTransfer}
          pick={pick}
        />
      ) : (
        <Loading what="Считаем, сколько счетов готово к переносу" />
      )}
      {note ? <p className="muted">{note}</p> : null}
      <Notice error={error ?? pick.error} />
      {summary ? <Missing groups={summary.missing_groups} /> : null}
      <Report transfer={transfer} />
    </section>
  )
}
